import { create } from 'zustand';
import { getBackendUrl } from '../config/environments';

/**
 * Enhanced Credits management store with backend integration
 */
const useCredits = create((set, get) => ({
  // State
  balance: 0,
  isLoading: false,
  lastUpdated: null,
  error: null,

  // Costs for different operations
  operationCosts: {
    imageGeneration: 1,
    videoGeneration: 5,
    aiChat: 1,
    templateGeneration: 2
  },

  // API base URL
  apiUrl: getBackendUrl(),

  // Fetch balance from backend
  fetchBalance: async (walletAddress) => {
    if (!walletAddress) return;
    
    set({ isLoading: true, error: null });
    
    try {
      const response = await fetch(`${get().apiUrl}/api/credits/${walletAddress}`);
      const data = await response.json();
      
      if (data.success) {
        set({ 
          balance: data.balance || 0, 
          lastUpdated: new Date(),
          isLoading: false 
        });
      } else {
        throw new Error(data.error || 'Failed to fetch balance');
      }
    } catch (error) {
      console.error('Error fetching credit balance:', error);
      set({ 
        error: error.message, 
        isLoading: false 
      });
    }
  },

  // Check if user has enough credits for an operation
  hasEnoughCredits: (amount = 1) => {
    return get().balance >= amount;
  },

  // Get cost for a specific operation
  getOperationCost: (operationType) => {
    const costs = get().operationCosts;
    return costs[operationType] || 1;
  },

  // Deduct credits for an operation (calls backend)
  deductCredits: async (walletAddress, amount, operationType, operationId = null) => {
    if (!walletAddress) {
      throw new Error('Wallet address is required');
    }

    // If amount is not provided, use operation type to get cost
    const creditAmount = typeof amount === 'number' ? amount : get().getOperationCost(amount);
    const opType = typeof amount === 'number' ? 'usage' : 'usage'; // Use 'usage' for all operations
    const opId = typeof amount === 'number' ? operationId : operationType;
    
    if (!get().hasEnoughCredits(creditAmount)) {
      throw new Error(`Insufficient credits. You need ${creditAmount} credits for ${opType}.`);
    }

    set({ isLoading: true, error: null });

    try {
      const response = await fetch(`${get().apiUrl}/api/credits/deduct`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          walletAddress,
          amount: creditAmount,
          operationType: opType,
          operationId: opId
        })
      });

      const data = await response.json();

      if (data.success) {
        set({ 
          balance: data.balance, 
          lastUpdated: new Date(),
          isLoading: false 
        });
        return { success: true, newBalance: data.balance };
      } else {
        throw new Error(data.error || 'Failed to deduct credits');
      }
    } catch (error) {
      console.error('Error deducting credits:', error);
      set({ 
        error: error.message, 
        isLoading: false 
      });
      throw error;
    }
  },

  // Add credits locally (used when payment is confirmed)
  addCredits: (amount) => {
    if (amount <= 0) return;
    set((state) => ({ 
      balance: state.balance + amount,
      lastUpdated: new Date()
    }));
  },

  // Set balance locally (used for initialization)
  setBalance: (amount) => {
    if (amount < 0) return;
    set({ 
      balance: amount, 
      lastUpdated: new Date() 
    });
  },

  // Clear error
  clearError: () => {
    set({ error: null });
  },

  // Reset store
  reset: () => {
    set({
      balance: 0,
      isLoading: false,
      lastUpdated: null,
      error: null
    });
  }
}));

export default useCredits; 